import { Inject, Injectable, Optional } from '@angular/core';

import { BetSlipType, KeyValueInformation } from '@bpos';
import { OverAskOfferDetails, SystemSlipDetails, WalletBucket } from '@bpos/common/bet-placement';
import { BetSlip, PlaceBetRequest } from '@bpos/v2/bet-placement';
import { AccountBalanceService, WalletType } from '@frontend/sports/account-balance/feature';
import { BetContextualTrackingConfig, BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { BetContextData, BetContextualDetails, SaveBetContextRequest, SlipInformation } from '@frontend/sports/types/betslip/request';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { sha512 } from 'js-sha512';
import { groupBy } from 'lodash-es';

import { BetslipUpsellConstants } from '../../../../../lib/betslip-base/models/betslip-upsell.model';
import { IBetslipState } from '../../../../base/store/state';
import { getModuleTypesOrder } from '../../../../base/utils/linear-utils';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../../core/picks/betslip-v1-pick';
import { BetslipV2StandardPick } from '../../../../core/picks/betslip-v2-standard-pick';
import { PickId } from '../../../../core/picks/pick-id';
import { BetslipV2HorseRaceWinParticipantPick } from '../../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import {
    BetContextAPIVersion,
    BetContextualInfo,
    BetContextualInfoMarketType,
    BetContextualInfoV2,
    SourceSystemType,
} from '../../../../model/bet-context';
import { DoubleBetPrevention } from '../../../../model/double-bet-prevention';
import { SlipTypeKeys } from '../../../bet-generation/betslip-system-type';
import { IBet, IBetslip, ISlip, SlipId } from '../../../bet-generation/models';
import { BetslipGeneratorInputBuilder } from '../../../bet-generation/services/betslip-generator-input-builder.service';
import { BetslipGeneratorService } from '../../../bet-generation/services/betslip-generator.service';
import { getBetslipTypeFromId, getTeaserSlipId } from '../../../bet-generation/services/slip.utils';
import { SingleSlipType, SlipType, SystemSlipType } from '../../../bet-generation/slip-type';
import { BetslipMarketSubTypeLogoService } from '../../../picks/services/betslip-market-sub-type-logo.service';
import BetslipSummaryBuilder from '../../../summary/services/betslip-summary-builder.service';
import { SystemKey } from '../../../system-bet/models';
import { convertSystem } from '../../../system-bet/utils/system-type.helper';
import BetValidationService from '../../../validation/services/composition/bets/bet-validation.service';
import { PlaceBetPayload } from '../../actions';
import { IBetPicks } from '../../models';
import { PickRequestMapperFactory } from './pick-request-mapper/pick-request-mapper.factory';
import { REQUEST_STRATEGIES_INJECTION_TOKEN, RequestStrategy } from './strategies/request-strategies';

@Injectable()
export default class PlaceBetRequestBuilder {
    private readonly linearBetslipTypesOrder: BetslipType[];

    constructor(
        private generatorInputBuilder: BetslipGeneratorInputBuilder,
        private betslipGenerator: BetslipGeneratorService,
        private accountBalanceService: AccountBalanceService,
        private betslipConfig: BetslipConfig,
        private pickRequestMapperFactory: PickRequestMapperFactory,
        @Optional() @Inject(REQUEST_STRATEGIES_INJECTION_TOKEN) private requestStrategies: RequestStrategy[],
        private userService: UserService,
        private betValidation: BetValidationService,
        private betContextualTrackingConfig: BetContextualTrackingConfig,
        private betslipSummaryBuilder: BetslipSummaryBuilder,
        private userSettings: SyncUserSettingsService,
        private betslipMarketSubTypeLogoService: BetslipMarketSubTypeLogoService,
    ) {
        const configuredOrder = this.betslipConfig.linearBetslipTypesOrder;
        this.linearBetslipTypesOrder = getModuleTypesOrder(configuredOrder);
    }

    build(state: IBetslipState, requestId: string): PlaceBetPayload {
        return this.buildFromPicks(state, requestId, state.picks.pickList);
    }

    buildEditBet(state: IBetslipState, requestId: string): PlaceBetPayload {
        return this.buildFromPicks(state, requestId, state.types.editBet.picks.pickList);
    }

    buildLinearQuickBetBuilder(state: IBetslipState, requestId: string, quickBetBuilderPick: PickId): PlaceBetPayload {
        const betBuilderSlips = this.getBetBuilderSlips(state, requestId, state.picks.pickList, quickBetBuilderPick);

        return this.buildFromSlips(state, requestId, betBuilderSlips.slips);
    }

    buildFromAllTypes(state: IBetslipState, requestId: string): PlaceBetPayload {
        const singleSlips = this.getLinearSingleSlips(state, requestId, state.picks.pickList);
        const comboSlips = this.getComboSlips(state, requestId, state.picks.pickList);
        const systemSlips = this.getSystemSlips(state, requestId, state.picks.pickList);
        const teaserSlips = this.getTeaserSlips(state, requestId, state.picks.pickList);
        const betBuilderSlips = this.getBetBuilderSlips(state, requestId, state.picks.pickList);

        const slips = [
            ...singleSlips.slips,
            ...comboSlips.slips,
            ...systemSlips.flatMap((s) => s.slips),
            ...teaserSlips.slips,
            ...betBuilderSlips.slips,
        ].filter((slip) => slip.stake);

        slips.forEach((s, i) => {
            s.index = ++i;
        });

        return this.buildFromSlips(state, requestId, slips);
    }

    private getLinearSingleSlips(state: IBetslipState, requestId: string, validationPicks: BetslipPick[]): IBetslip {
        const restrictions = this.betValidation.getRestrictions(validationPicks, false, false);
        const input = this.generatorInputBuilder.getLinearSingleBetsCalculatorInput(state);

        return this.betslipGenerator.calculate(input, { restrictions });
    }

    private getComboSlips(state: IBetslipState, requestId: string, validationPicks: BetslipPick[]): IBetslip {
        const restrictions = this.betValidation.getRestrictions(validationPicks, false, false);
        const input = this.generatorInputBuilder.getComboBetsCalculatorInput(state);

        return this.betslipGenerator.calculate(input, { restrictions });
    }

    private getSystemSlips(state: IBetslipState, requestId: string, validationPicks: BetslipPick[]): IBetslip[] {
        const restrictions = this.betValidation.getRestrictions(validationPicks, true, false);
        const typeStakes = state.types.systemBet.linearTypeStakes;
        const types = Object.values(typeStakes).map((stake) => stake.slipType);

        const inputs = types.map((t) => this.generatorInputBuilder.getSystemSlipsCalculatorInput(state, [t]));

        return inputs.map((i) => this.betslipGenerator.calculate(i, { restrictions }));
    }

    private getTeaserSlips(state: IBetslipState, requestId: string, validationPicks: BetslipPick[]): IBetslip {
        const restrictions = this.betValidation.getRestrictions(validationPicks, false, true);
        const input = this.generatorInputBuilder.getTeaserCalculatedInput(state);

        return this.betslipGenerator.calculate(input, { restrictions });
    }

    private getBetBuilderSlips(state: IBetslipState, requestId: string, validationPicks: BetslipPick[], targetPickId?: PickId | undefined): IBetslip {
        const restrictions = this.betValidation.getRestrictions(validationPicks, false, false);

        const input = this.generatorInputBuilder.getBetBuilderBetsCalculatorInput(state, targetPickId);

        return this.betslipGenerator.calculate(input, { restrictions });
    }

    private buildFromPicks(state: IBetslipState, requestId: string, validationPicks: BetslipPick[]): PlaceBetPayload {
        const isSystemBet = state.types.base.currentSelectedType === BetslipType.System;
        const isTeaserBet = state.types.base.currentSelectedType === BetslipType.Teaser;
        const restrictions = this.betValidation.getRestrictions(validationPicks, isSystemBet, isTeaserBet);

        const input = this.generatorInputBuilder.getCalculatorInput(state);
        const output = this.betslipGenerator.calculate(input, { restrictions });

        return this.buildFromSlips(state, requestId, output.slips);
    }

    buildFromSlips(state: IBetslipState, requestId: string, slips: ISlip[]): PlaceBetPayload {
        const request = <PlaceBetRequest>{
            requestId,
            betSlips: [] as BetSlip[],
            doubleBetPreventionMode: this.betslipConfig.isDoubleBetPreventionEnabled
                ? state.placeBet.doubleBetPrevention
                : DoubleBetPrevention.Disabled,
            language: '', //Ivelina Zaharieva: this is a required field in BPOS contract and if it is missing will cause a deserialization issue in Frontend.Sports
            additionalInformation: {
                informationItems: [] as KeyValueInformation[],
            },
        };

        const requestPicks: IBetPicks = {
            picks: {},
            groups: {},
        };
        const slipIndexByBetIndex: { [betIndex: number]: { slipIndex: number; betIndex: number } } = {};
        for (const slip of slips) {
            const betslip: BetSlip = {
                betSlipType: this.convertBetSlipType(slip.type),
                index: slip.index,
                stake: {
                    amount: this.betslipSummaryBuilder.getEffectiveStake(slip.stake) || 0,
                    currency: this.userService.currency,
                },
                stakeTaxation: this.userSettings.isTurnoverTaxApplied ? this.betslipSummaryBuilder.getStakeTaxation(slip.stake) : null,
                bets: [],
                betGroups: [],
                additionalInformation: {
                    informationItems: [],
                },
                edsPromoTokens: [],
                oddsAcceptanceMode: state.settings.oddsAcceptance,
                promoTokens: [],
                walletBucket: this.getWalletBucket(),
                overAskOfferDetails: null as unknown as OverAskOfferDetails,
                checkOrder: this.getBetOrder(slip.slipId),
            } as unknown as BetSlip;
            const slipPicks = Object.values(slip.slipPicks);
            let betIndex = slip.type instanceof SingleSlipType ? slip.index : 100 * slip.index;
            for (const slipPick of slipPicks) {
                const pickRequestMapper = this.pickRequestMapperFactory.create(slipPick.pick);
                const { nextIndex } = pickRequestMapper.appendBetDetails(betslip, slipPick, betIndex, requestPicks, state, {
                    isTeaser: slip.slipId === getTeaserSlipId(),
                });
                slipIndexByBetIndex[betIndex] = { betIndex: betIndex, slipIndex: slip.index };
                betIndex = nextIndex;
            }

            betslip.systemSlipDetails = this.getSystemInfo(slip, requestPicks);
            request.betSlips.push(betslip);
        }

        const requestStrategies = this.requestStrategies?.filter((rs) => rs.isActive(state)) || [];
        const trackBetContext = this.betContextualTrackingConfig.isEnabled
            ? (this.getBetContextualData(
                  state,
                  request,
                  state.types.base.currentSelectedType,
                  requestPicks,
                  false,
                  slipIndexByBetIndex,
              ) as BetContextualDetails[])
            : [];
        const trackBetContextV2 = this.isBetContextualTrackingV2Enabled()
            ? (this.getBetContextualData(
                  state,
                  request,
                  state.types.base.currentSelectedType,
                  requestPicks,
                  true,
                  slipIndexByBetIndex,
              ) as SaveBetContextRequest)
            : undefined;
        for (const strategy of requestStrategies) {
            strategy.updateRequest(request, state, slips, requestPicks);
        }

        return { request, requestPicks, trackBetContext, trackBetContextV2, slips };
    }

    private getBetContextualData(
        betslipState: IBetslipState,
        request: PlaceBetRequest,
        betSlipType: BetslipType | null,
        betRequest: IBetPicks,
        isBetContextualDataV2: boolean,
        slipIndexByBetIndex: { [betIndex: number]: { slipIndex: number; betIndex: number } },
    ): SaveBetContextRequest | BetContextualDetails[] {
        const contextDataRequest: BetContextualDetails[] = [];
        const contextDataRequest_SingleBet: BetContextualDetails[] = [];

        const createBetContextualDetails = <T>(
            requestId: string,
            betSlipKey: number,
            betContext: T,
            parentRequestId?: string,
        ): BetContextualDetails => ({
            requestId,
            betSlipKey,
            betContextualData: JSON.stringify(betContext),
            parentRequestId,
        });

        const createSaveBetContextRequest = (requestId: string, slips: SlipInformation[], parentRequestId?: string): SaveBetContextRequest => ({
            requestId,
            slips,
            parentRequestId,
        });

        if (betslipState.overAskState?.offerId) {
            const betSlipKey = betslipState.overAskState.bets[0].index;
            const parentRequestId = betslipState.overAskState.parentRequestId;
            if (isBetContextualDataV2) {
                return createSaveBetContextRequest(request.requestId, [{ betContextData: [], betSlipKey }] as SlipInformation[], parentRequestId);
            }

            return [createBetContextualDetails(request.requestId, betSlipKey, {}, parentRequestId)] as BetContextualDetails[];
        }
        const combinedPicks = betRequest.picks;
        const picksCount = Object.keys(combinedPicks).length;
        // The way BetContextData is collected is wrong, it relies on betslip state while it should mostly rely on the request with its betSlips and use the betslipState as a lookup
        // The isSingleBet logic has been change to take into consideration the case when the "Sportcast as Combo" feature is enabled and we have just one Sportcast BetBuilder in the betslip.
        // In this case, the betSlipType will be Combo although the bet placement request is overriding this and placing it as Single. Since we don't use the request data here, we have to implement
        // a similar override for BetContextData

        //betContextDataRequest: Type is important to recognize data "0" - betContextualData, "1" - 2UPmarketContext.

        const isSingleBet =
            betSlipType === BetslipType.Single ||
            (this.betslipConfig.isSportcastAsComboEnabled &&
                picksCount === 1 &&
                Object.values(combinedPicks).some((pick) => BetslipBetBuilderPick.isPick(pick)));

        const slips: SlipInformation[] = [];
        const groupedPicksBySlipIndex = groupBy(slipIndexByBetIndex, (x) => x.slipIndex);
        const createBetContextualData = <T>(betContext: T, type: number): BetContextData => ({
            data: JSON.stringify(betContext),
            type: type,
            version: 1,
        });

        for (const slipIndexes of Object.values(groupedPicksBySlipIndex)) {
            const comboBetContextualInfos: BetContextualInfo[] = [];
            let slip: SlipInformation;
            const additionalBetContextualData: BetContextualInfoV2[] = [];

            slipIndexes.forEach((slipIndex) => {
                const pickId = slipIndex.betIndex;
                const pick = combinedPicks[+pickId];
                const betContext = this.getTrackBetContextualData(request, pick, +pickId);
                let optionId = this.getOptionId(pick);

                if (isSingleBet) {
                    // single savebetcontext v2
                    if (isBetContextualDataV2) {
                        slip = {
                            betSlipKey: +pickId,
                            betContextData: [createBetContextualData<BetContextualInfo>(betContext, 0)],
                        };

                        if (this.additionalTrackingEnabled(pick)) {
                            slip.betContextData.push(createBetContextualData<BetContextualInfoV2>(this.twoUpMarketTrackingData(optionId), 1));
                        }
                        slips.push(slip);
                    } else {
                        // single betcontextualdetails v1
                        contextDataRequest_SingleBet.push(createBetContextualDetails<BetContextualInfo>(request.requestId, +pickId, betContext));
                    }
                } else {
                    comboBetContextualInfos.push(betContext);
                    if (this.additionalTrackingEnabled(pick)) {
                        additionalBetContextualData.push(this.twoUpMarketTrackingData(optionId));
                    }
                }
            });

            if (!isSingleBet) {
                // combo savebetcontext v2
                if (isBetContextualDataV2) {
                    slip = {
                        betSlipKey: slipIndexes[0].slipIndex,
                        betContextData: [createBetContextualData<BetContextualInfo[]>(comboBetContextualInfos, 0)],
                    };

                    if (additionalBetContextualData.length) {
                        slip.betContextData.push(createBetContextualData<BetContextualInfoV2[]>(additionalBetContextualData, 1));
                    }
                    slips.push(slip);
                } else {
                    // combo betcontextualdetails v1
                    contextDataRequest.push(createBetContextualDetails<BetContextualInfo[]>(request.requestId, 1, comboBetContextualInfos));
                }
            }
        }

        // single and combo savebetcontext v2
        if (isBetContextualDataV2) {
            return createSaveBetContextRequest(request.requestId, slips);
        }

        // single and combo betcontextualdetails v1
        return isSingleBet ? contextDataRequest_SingleBet : contextDataRequest;
    }

    private getOptionId(pick: BetslipPick): number | undefined {
        if (pick instanceof BetslipV2HorseRaceWinParticipantPick) {
            return pick.acceptedPrice.price?.id;
        } else {
            return (pick as BetslipV1Pick | BetslipV2StandardPick | BetslipBetBuilderPick).optionId;
        }
    }

    private twoUpMarketTrackingData(optId?: number): BetContextualInfoV2 {
        return {
            optionId: optId,
            marketType: BetContextualInfoMarketType.TwoUpMarket,
        };
    }

    private isBetContextualTrackingV2Enabled(): boolean {
        return this.betContextualTrackingConfig.isEnabled && this.betContextualTrackingConfig.betContextAPIVersion === BetContextAPIVersion.V2;
    }

    private getBetOrder(slipId: SlipId): number {
        const MAX_ORDER = 100;
        const betType = getBetslipTypeFromId(slipId);

        return betType ? this.linearBetslipTypesOrder.indexOf(betType) : MAX_ORDER;
    }

    private additionalTrackingEnabled(pick: BetslipPick | undefined): boolean {
        return (
            this.betContextualTrackingConfig.enableExtendedDataTracking.some((x) => x === BetContextualInfoMarketType.TwoUpMarket) &&
            !!this.betslipMarketSubTypeLogoService.getMarketSubTypeSignpostingText(pick)
        );
    }

    private getTrackBetContextualData(request: PlaceBetRequest, pick: BetslipPick, betIndex: number): BetContextualInfo {
        let optId: number | undefined;
        const trackingSource = pick.tracking?.source;

        if (pick instanceof BetslipV2HorseRaceWinParticipantPick) {
            optId = pick.acceptedPrice.price?.id;
        } else {
            optId = (pick as BetslipV1Pick | BetslipV2StandardPick | BetslipBetBuilderPick).optionId;
        }

        let sourceSystem = 0;

        request.betSlips.forEach((betSlip) => {
            const selectedBet =
                betSlip.bets.find((bet) => bet.betDetails.some((betDetail) => betDetail.id === optId?.toString())) ||
                betSlip.bets.find((bet) => bet.picks.some((p) => p.id === optId?.toString()));
            sourceSystem = selectedBet ? SourceSystemType[selectedBet.betModel] : 0;
        });

        const pageName = trackingSource?.split('/')[0] ?? '';
        const isUpsell = pick.tracking?.sourceOfPick === SourceOfPick.Upsell;

        if (this.betContextualTrackingConfig.allowedPages.includes(pageName) && pick.tracking.additional && !isUpsell) {
            return {
                sourceSystem,
                betIndex,
                optionId: optId,
                pageName,
                moduleCustomName: pick.tracking.additional[trackingConstants.COMPONENT_MODULE_CUSTOM_NAME],
                moduleLayout: pick.tracking.additional[trackingConstants.COMPONENT_PAGE_LAYOUT],
                modulePosition: pick.tracking.additional[trackingConstants.COMPONENT_MODULE_POSITION],
                moduleType: pick.tracking.additional[trackingConstants.COMPONENT_MODULE_NAME],
            };
        }

        return {
            sourceSystem,
            betIndex,
            optionId: optId,
            pageName: isUpsell ? BetslipUpsellConstants.TrackingSource : pageName,
        };
    }

    private getWalletBucket(): WalletBucket | undefined {
        const wallet = this.accountBalanceService.selectedWallet;
        if (!wallet || wallet.type === WalletType.Any) {
            return;
        }
        switch (wallet.type) {
            case WalletType.Main:
                return WalletBucket.Main;
            case WalletType.PayPal:
                return WalletBucket.PayPal;
        }
    }

    private convertBetSlipType(slipType: SlipType): BetSlipType {
        if (slipType.key === SlipTypeKeys.Single) {
            return BetSlipType.Single;
        } else if (slipType.key === SlipTypeKeys.Combo) {
            return BetSlipType.Combo;
        } else {
            return BetSlipType.System;
        }
    }

    private getSystemInfo(slip: ISlip, requestPicks: IBetPicks): SystemSlipDetails {
        if (!(slip.type instanceof SystemSlipType)) {
            return null as unknown as SystemSlipDetails;
        }

        const systemDetails: SystemSlipDetails = {
            systemType: convertSystem(slip.type.key),
            excludedCombinationsBitmap: '',
            matrixHash: '',
        };

        if (slip.type.key === SystemKey.MOfN) {
            systemDetails.arity = slip.type.basis[0];
            systemDetails.excludedCombinationsBitmap = this.calculateBitmap(slip) as unknown as string; // our contracts generator wrongly maps IEnumerable<byte> as string
            systemDetails.matrixHash = this.calculateHash(slip, requestPicks);
        }

        return systemDetails;
    }

    /**
     * Creates a bitmap with the invalid picks so that BPS can check if the invalid picks
     * on the BPS backend are the same as the ones of the MS2 frontend
     *
     * @param slip = the slip for which we're generating the bitmap
     * @returns a bitmap a form of a byte array
     */
    private calculateBitmap(slip: ISlip): number[] {
        const indexedBets = slip.bets.filter((b) => typeof b.index !== 'undefined');
        const invalidBets = indexedBets.filter((bet) => !bet.isValid);
        if (!invalidBets.length) {
            return [];
        }

        const maxIndex = Math.max(...indexedBets.map((bet) => bet.index!));
        const invalidBetsBitmask: number[] = new Array(Math.ceil((maxIndex + 1) / 8)).fill(0);

        for (const invalidBet of this.filterIndexedBets(invalidBets)) {
            const bitMaskProps = this.getCombinationBitmap(invalidBet.index!);
            // eslint-disable-next-line no-bitwise
            invalidBetsBitmask[bitMaskProps.byteIndex] |= bitMaskProps.byteValue;
        }

        return invalidBetsBitmask.reverse();
    }

    private filterIndexedBets(bets: IBet[]): IBet[] {
        return bets.filter((b) => typeof b.index !== 'undefined');
    }

    private getCombinationBitmap(index: number): { byteIndex: number; byteValue: number } {
        return {
            byteIndex: Math.floor(index / 8),
            // eslint-disable-next-line no-bitwise
            byteValue: 1 << index % 8,
        };
    }

    /**
     * Creates a hash with the valid combinations so that the api services can verify
     * if the valid combinations on their side are the same as those on the MS2 frontend
     *
     * @param slip
     * @return base64 string
     */
    private calculateHash(slip: ISlip, requestPicks: IBetPicks): string {
        const betNumberMap = this.betNumberMap(requestPicks);
        const validBets = slip.bets.filter((bet) => bet.isValid);
        const picksString = this.filterIndexedBets(validBets)
            .map((b) => {
                return b.legs.map((l) => betNumberMap[l.pickId.toString()]).join(',');
            })
            .join('|');
        const hexHash = sha512(picksString);

        return this.hexToBase64(hexHash);
    }

    private betNumberMap(requestPicks: IBetPicks): { [pickId: string]: string } {
        const result: { [pickId: string]: string } = {};

        for (const betIndex in requestPicks.picks) {
            const pickId = requestPicks.picks[betIndex].id.toString();
            result[pickId] = betIndex;
        }

        return result;
    }

    /**
     * Parses a binary array represented by a string of hex characters to a base64 string
     *
     * @param str = a string with characters within the range [0-9]U[a-f]
     * @returns parsed base64 string
     */
    private hexToBase64(str: string): string {
        return btoa(
            String.fromCharCode.apply(
                null,
                str
                    .replace(/\r|\n/g, '')
                    .replace(/([\da-fA-F]{2}) ?/g, '0x$1 ')
                    .replace(/ +$/, '')
                    .split(' '),
            ),
        );
    }
}
